import type { Database } from '../types/supabase';
import { supabase } from './supabase';

if (!supabase) {
  throw new Error('Supabase client is not initialized');
}

export interface ServiceCategory {
  id: string;
  business_id: string;
  name: string;
  description: string;
  order_index: number;
  created_at: string;
  updated_at: string;
}

interface PriceOption {
  id?: string;
  service_id: string;
  name: string;
  price: number;
  duration_minutes: number;
  created_at?: string;
  updated_at?: string;
}

export interface Service {
  id: string;
  business_id: string;
  name: string;
  description: string | null;
  category_id?: string;
  color?: string;
  tax_rate_id?: string | null;
  tax_exempt?: boolean;
  created_at: string;
  updated_at: string;
  category?: ServiceCategory;
  price_options?: PriceOption[];
  addons?: ServiceAddon[];
}

export interface ServiceAddon {
  id: string;
  business_id?: string;
  service_id?: string;
  addon_service_id: string;
  name: string;
  description: string | null;
  price: number;
  duration_minutes: number;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export interface CreateServiceAddonParams {
  service_id: string;
  business_id: string;
  name: string;
  description?: string;
  price: number;
  duration_minutes: number;
  addon_service_id?: string;
  order_index?: number;
}

export async function getServiceCategories(businessId: string) {
  const { data, error } = await supabase
    .from('service_categories')
    .select('*')
    .eq('business_id', businessId)
    .order('order_index', { ascending: true });

  if (error) throw error;
  return data as ServiceCategory[];
}

export async function getServices(businessId: string) {
  const { data, error } = await supabase
    .from('services')
    .select(`
      *,
      category:service_categories(*),
      price_options:price_options(*)
    `)
    .eq('business_id', businessId)
    .order('order_index', { ascending: true });

  if (error) throw error;
  return data as Service[];
}

async function getService(id: string) {
  const { data, error } = await supabase
    .from('services')
    .select(`
      *,
      category:service_categories(*),
      price_options:price_options(*)
    `)
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as Service;
}

export async function createServiceCategory(category: Omit<ServiceCategory, 'id' | 'created_at' | 'updated_at'>) {
  const { data, error } = await supabase
    .from('service_categories')
    .insert([category])
    .select()
    .single();

  if (error) throw error;
  return data as ServiceCategory;
}

export async function updateServiceCategory(id: string, updates: Partial<ServiceCategory>) {
  const { data, error } = await supabase
    .from('service_categories')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as ServiceCategory;
}

export async function deleteServiceCategory(id: string) {
  const { error } = await supabase
    .from('service_categories')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export async function createService(
  service: Omit<Service, 'id' | 'created_at' | 'updated_at' | 'category' | 'price_options'>,
  priceOptions: Omit<PriceOption, 'id' | 'service_id' | 'created_at' | 'updated_at'>[],
  addons?: Omit<ServiceAddon, 'id' | 'created_at' | 'updated_at'>[]
) {
  // Create service
  const { data: serviceData, error: serviceError } = await supabase
    .from('services')
    .insert([{
      business_id: service.business_id,
      name: service.name,
      description: service.description,
      category_id: service.category_id,
      color: service.color,
      tax_rate_id: service.tax_rate_id,
      tax_exempt: service.tax_exempt,
      created_at: new Date().toISOString()
    }])
    .select()
    .single();

  if (serviceError) throw serviceError;

  // Create price options
  if (priceOptions.length > 0) {
    const { error: pricesError } = await supabase
      .from('price_options')
      .insert(
        priceOptions.map(option => ({
          service_id: serviceData.id,
          name: option.name,
          price: option.price,
          duration_minutes: option.duration_minutes
        }))
      );

    if (pricesError) throw pricesError;
  }

  // Create service add-ons
  if (addons && addons.length > 0) {
    const { error: addonsError } = await supabase
      .from('service_addons')
      .insert(
        addons.map((addon, index) => ({
          business_id: service.business_id,
          service_id: serviceData.id,
          addon_service_id: serviceData.id,
          name: addon.name,
          description: addon.description,
          price: addon.price,
          duration_minutes: addon.duration_minutes,
          order_index: index
        }))
      );

    if (addonsError) throw addonsError;
  }

  return getService(serviceData.id);
}

export async function updateService(id: string, updates: Partial<Service> & { price_options?: { name: string; price: number; duration_minutes: number }[] }) {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  try {
    // Extract price_options from updates
    const { price_options, ...serviceUpdates } = updates;

    // Only update service if there are service updates
    if (Object.keys(serviceUpdates).length > 0) {
      const { error: serviceError } = await supabase
        .from('services')
        .update({
          ...serviceUpdates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (serviceError) {
        throw serviceError;
      }
    }

    // If price_options are provided, update them
    if (price_options) {
      // First, delete all existing price options for this service
      const { error: deleteError } = await supabase
        .from('price_options')
        .delete()
        .eq('service_id', id);

      if (deleteError) {
        throw deleteError;
      }

      // Then insert the new price options
      if (price_options.length > 0) {
        const { error: priceOptionsError } = await supabase
          .from('price_options')
          .insert(
            price_options.map(option => ({
              service_id: id,
              name: option.name,
              price: option.price,
              duration_minutes: option.duration_minutes
            }))
          );

        if (priceOptionsError) {
          throw priceOptionsError;
        }
      }
    }

    return { data: await getService(id), error: null };
  } catch (error) {
    console.error('Error updating service:', error);
    return { data: null, error };
  }
}

// Older version of updateService for backward compatibility
export async function updateServiceLegacy({
  id,
  name,
  description,
  price,
  tax_rate,
  tax_exempt,
  duration_minutes,
  addons
}: {
  id: string;
  name: string;
  description: string | null;
  price: number;
  tax_rate: number;
  tax_exempt: boolean;
  duration_minutes: number;
  addons?: ServiceAddon[];
}) {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  try {
    // Update the service
    const { data: updatedService, error: serviceError } = await supabase
      .from('services')
      .update({
        name,
        description,
        price,
        tax_rate,
        tax_exempt,
        duration_minutes,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (serviceError) {
      throw serviceError;
    }

    // If addons are provided, update them
    if (addons) {
      // First, delete all existing addons for this service
      const { error: deleteError } = await supabase
        .from('service_addons')
        .delete()
        .eq('service_id', id);

      if (deleteError) {
        throw deleteError;
      }

      // Then insert the new addons
      if (addons.length > 0) {
        const { error: addonsError } = await supabase
          .from('service_addons')
          .insert(
            addons.map((addon, index) => ({
              service_id: id,
              name: addon.name,
              description: addon.description,
              price: addon.price,
              duration_minutes: addon.duration_minutes,
              order_index: index,
              business_id: updatedService.business_id
            }))
          );

        if (addonsError) {
          throw addonsError;
        }
      }
    }

    return { data: updatedService, error: null };
  } catch (error) {
    console.error('Error updating service:', error);
    return { data: null, error };
  }
}

export async function deleteService(id: string) {
  const { error } = await supabase
    .from('services')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export async function getServiceAddons(serviceId: string) {
  const { data, error } = await supabase
    .from('service_addons')
    .select('*')
    .eq('service_id', serviceId)
    .order('order_index', { ascending: true });

  if (error) throw error;
  return data as ServiceAddon[];
}

export async function updateServiceAddon(id: string, updates: Partial<ServiceAddon>) {
  const { error } = await supabase
    .from('service_addons')
    .update(updates)
    .eq('id', id);

  if (error) throw error;
}

export async function deleteServiceAddon(id: string) {
  const { error } = await supabase
    .from('service_addons')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export async function createServiceAddon(params: CreateServiceAddonParams): Promise<ServiceAddon> {
  if (!supabase) throw new Error('Supabase client not initialized');

  // Ensure addon_service_id is set - use service_id as fallback
  const addon = {
    ...params,
    addon_service_id: params.addon_service_id || params.service_id
  };

  const { data, error } = await supabase
    .from('service_addons')
    .insert([addon])
    .select()
    .single();

  if (error) throw error;
  return data;
}